import React from "react";
import { useNavigate } from "react-router-dom";
import { Box, Button, Grid, Stack, useMediaQuery, useTheme } from "@mui/material";
import OpenInFullIcon from "@mui/icons-material/OpenInFull";
import { RightPanel } from "../../../components/RightPanel";
import { IActionItem } from "../../../redux/actionItems";
import { REACT_APP_NAV_ROUTES } from "../../../router/AppNavigation";
import { ChatLeadForm } from "../Component/ChatLeadForm";
import { ActivityRoot } from "../../../components/Activity/ActivityRoot";
import { MODULE_NAMES } from "../../../constants/enums";
import { EditLeadDetails } from "../../dashboard/Dashboard/components/ViewLeadDeatils/EditLeadDetails";
import { useDefaultOngoingStatus } from "../../leads/hooks/useDefaultOngoingStatus";
import { ILead, initialLead, initialMultipleEvent } from "../../../redux/leads/leads.types";
import { getCountryCode, replaceCountryCode } from "../../../helpers/replaceConutryCode";

export interface IChatLeadQuickEditRightPanelProps {
  open: boolean;
  actionItem: IActionItem;
  onClose: () => void;
  onComplete: () => void;
}

export const ChatLeadQuickEditRightPanel: React.FC<
  IChatLeadQuickEditRightPanelProps
> = ({ open, actionItem, onClose, onComplete }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [showActivityRoot, setShowActivityRoot] = React.useState(false);
  const defaultOngoingStatus = useDefaultOngoingStatus();

  const [openEdit, setOpenEdit] = React.useState<{
    lead: ILead | null;
    chatActionItem: IActionItem | null;
  }>({
    lead: null,
    chatActionItem: null,
  });

  const convertActionItemIntoLeadDetails = React.useCallback(
    (item: IActionItem): ILead => {
      const ongoingName = defaultOngoingStatus?.name || "ONGOING";
      const ongoingDriveStatusUuid = defaultOngoingStatus?.drive_status_uuid || "";
      return {
        ...initialLead,
        name_of_guest: item.name,
        contact_number_one: replaceCountryCode(item.mobile_no),
        country_code: getCountryCode(item.mobile_no),
        remarks: item.description,
        date_of_visit: item.create_ts,
        source: item.source as string,
        handled_by_employee_uuid: item.handled_by_employee_uuid as string,
        handled_by_employee: item.handled_by_employee as string,
        lead_source_uuid: item.lead_source_uuid ?? "",
        priority: "HOT",
        multiple_events_list: [
          {
            ...initialMultipleEvent,
            status: ongoingName,
            drive_status_uuid: ongoingDriveStatusUuid,
            event_time_uuid: item.event_time_uuid as string,
            event_time: item.event_time as string,
            event_type: item.event_type as string,
            followup_date: item.followup_date as string,
            function_date: item.function_date as string,
            function_month: item.function_month as string,
            guest_budget: item.guest_budget as string,
            number_of_guests: item.number_of_guests as number,
            number_of_rooms: item.number_of_rooms as number,
            preferred_venue_uuid: item.preferred_venue_uuid as string,
            preferred_venue: item.preferred_venue as string,
          },
        ],
      };
    },
    [defaultOngoingStatus],
  );

  const handleCreateLead = () => {
    const lead = convertActionItemIntoLeadDetails(actionItem);
    setOpenEdit({
      lead,
      chatActionItem: actionItem,
    });
  };

  const actionButtons = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {actionItem.status !== "LEAD" && (
        <Button
          size="small"
          variant="outlined"
          color="inherit"
          onClick={(e) => {
            e.stopPropagation();
            handleCreateLead();
          }}
          sx={{ color: "#fff", borderColor: "#fff", mr: 1, height: 28, fontSize: 12 }}
        >
          Create Event Lead
        </Button>
      )}
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        onClick={(e) => {
          e.stopPropagation();
          setShowActivityRoot(!showActivityRoot);
        }}
        sx={{ color: "#fff", borderColor: "#fff", mr: 1, height: 28, fontSize: 12 }}
      >
        {showActivityRoot ? "Hide Activity" : "Show Activity"}
      </Button>
      <Button
        size="small"
        variant="outlined"
        color="inherit"
        startIcon={<OpenInFullIcon sx={{ fontSize: 14 }} />}
        onClick={(e) => {
          e.stopPropagation();
          navigate(
            `${REACT_APP_NAV_ROUTES.leads.chatLeads}/manage/${actionItem.action_item_uuid}`,
          );
        }}
        sx={{ color: "#fff", borderColor: "#fff", height: 28, fontSize: 12 }}
      >
        Full View
      </Button>
    </Box>
  );

  return (
    <RightPanel
      open={open}
      heading="Quick Edit External Lead"
      width="90%"
      onClose={onClose}
      hideScrollbar
      topRightButtons={!isMobile ? actionButtons : undefined}
      subHeading={isMobile ? actionButtons : undefined}
    >
      <Grid container spacing={2}>
        <Grid item xs={12} md={showActivityRoot ? 7.5 : 12}>
          <ChatLeadForm
            initial={actionItem}
            onSaved={() => {
              onComplete();
              onClose();
            }}
            onCancel={onClose}
            renderFooter={({ submit, cancel, isSubmitting }) => (
              <Stack
                direction="row"
                spacing={2}
                justifyContent="flex-end"
                mt={3}
              >
                <Button
                  variant="outlined"
                  onClick={cancel}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  onClick={submit}
                  disabled={isSubmitting}
                >
                  Save
                </Button>
              </Stack>
            )}
          />
        </Grid>

        {showActivityRoot && (
          <Grid item xs={12} md={4.5}>
            <Box
              sx={{
                position: { xs: "static", md: "sticky" },
                top: { xs: "auto", md: "10px" },
                zIndex: 10,
                height: { xs: "auto", md: "calc(100vh - 140px)" },
                display: "flex",
                flexDirection: "column",
              }}
            >
              <ActivityRoot
                onClose={() => setShowActivityRoot(false)}
                moduleInformation={{
                  moduleId: actionItem.action_item_uuid as string,
                  moduleName: MODULE_NAMES.CHAT_LEAD,
                  subModuleName: MODULE_NAMES.CHAT_LEAD,
                  refColumnName: "action_item_uuid",
                }}
                activity={{
                  whatsAppChat: {
                    phone: actionItem.mobile_no,
                    customerName: actionItem.name,
                  },
                }}
              />
            </Box>
          </Grid>
        )}
      </Grid>

      {openEdit.lead && (
        <EditLeadDetails
          open={true}
          title="External Lead Info"
          leadDetails={openEdit.lead}
          chatActionItem={openEdit.chatActionItem}
          onClose={() =>
            setOpenEdit({
              lead: null,
              chatActionItem: null,
            })
          }
          onComplete={() => {
            setOpenEdit({
              lead: null,
              chatActionItem: null,
            });
            onComplete();
            onClose();
          }}
        />
      )}
    </RightPanel>
  );
};
